import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { ConnectionBanner } from "@/components/ui/ConnectionBanner";
import { SessionWarningDialog } from "@/components/ui/SessionWarningDialog";

// ── Eager-loaded routes ────────────────────────────────────────────────
// Only the AdminLayout shell stays eager — everything else is lazy so
// the initial bundle stays small. React.lazy + Suspense already wraps
// the whole tree, so the user sees a sub-100ms loader on the first
// route hit and the chunk is cached for subsequent navigations.
import AdminLayout from "@/components/layouts/AdminLayout";

// ── Lazy-loaded routes ─────────────────────────────────────────────────
// React.lazy splits each into its own JS chunk; the route only pays for
// its bundle when the user navigates there.
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const PublicResultsPage = lazy(() => import("@/pages/public/PublicResultsPage"));
const KlinilogLandingPage = lazy(() => import("@/pages/public/KlinilogLandingPage"));
const KlinilogLeadsPage = lazy(() => import("@/pages/admin/KlinilogLeadsPage"));
const LoginPage = lazy(() => import("@/pages/admin/LoginPage"));
// `webpackPrefetch: true` on the post-login critical path: the browser
// downloads these chunks during idle time (typically while the user is
// typing their password) so navigation feels instant after the click.
const AgendaPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/admin/AgendaPage"));
const DashboardPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/admin/DashboardPage"));
const DoctorDashboardPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/admin/DoctorDashboardPage"));
const BookingSuccessPage = lazy(() => import("@/pages/BookingSuccessPage"));
const BookingCancelPage = lazy(() => import("@/pages/BookingCancelPage"));
const CancelAppointmentPage = lazy(() => import("@/pages/CancelAppointmentPage"));
const PatientConfirmPage = lazy(() => import("@/pages/PatientConfirmPage"));
const RemoteSignaturePage = lazy(() => import("@/pages/public/RemoteSignaturePage"));
const ReviewConfirmPage = lazy(() => import("@/pages/ReviewConfirmPage"));
const MobileUploadPage = lazy(() => import("@/pages/MobileUploadPage"));
const AcceptBudgetPage = lazy(() => import("@/pages/public/AcceptBudgetPage"));
const CompliancePublicPage = lazy(() => import("@/pages/public/CompliancePublicPage"));
const AppPrivacyPage = lazy(() => import("@/pages/public/AppPrivacyPage"));
const ErasureRequestPage = lazy(() => import("@/pages/public/ErasureRequestPage"));
const PublicDocumentPage = lazy(() => import("@/pages/public/PublicDocumentPage"));
const PatientPortalPage = lazy(() => import("@/pages/public/PatientPortalPage"));
const PatientAreaLayout = lazy(() => import("@/layouts/PatientAreaLayout"));
const PatientLoginPage = lazy(() => import("@/pages/patient/PatientLoginPage"));
const PatientRegisterPage = lazy(() => import("@/pages/patient/PatientRegisterPage"));
const PatientMagicLinkEnterPage = lazy(() => import("@/pages/patient/PatientMagicLinkEnterPage"));
const PatientHomePage = lazy(() => import("@/pages/patient/PatientHomePage"));
const PatientDoctorPage = lazy(() => import("@/pages/patient/PatientDoctorPage"));
const VoluntaryConfirmPage = lazy(() =>
  import("@/pages/VoluntaryWaitlistPages").then((m) => ({ default: m.VoluntaryConfirmPage })));
const VoluntaryOffersPage = lazy(() =>
  import("@/pages/VoluntaryWaitlistPages").then((m) => ({ default: m.VoluntaryOffersPage })));
const TestimonialsAdminPage = lazy(() => import("@/pages/admin/TestimonialsAdminPage"));
const PatientContentPage = lazy(() => import("@/pages/patient/PatientContentPage"));
const PatientArticlePage = lazy(() => import("@/pages/patient/PatientArticlePage"));
const PatientBraGuidePage = lazy(() => import("@/pages/patient/PatientBraGuidePage"));
const PatientRiskCalculatorPage = lazy(() => import("@/pages/patient/PatientRiskCalculatorPage"));
const PatientSelfExamPage = lazy(() => import("@/pages/patient/PatientSelfExamPage"));
const PatientPostopDiaryPage = lazy(() => import("@/pages/patient/PatientPostopDiaryPage"));
const PatientNotificationsPage = lazy(() => import("@/pages/patient/PatientNotificationsPage"));
const PublicArticlePage = lazy(() => import("@/pages/public/PublicArticlePage"));
const PatientUrgentTriagePage = lazy(() => import("@/pages/patient/PatientUrgentTriagePage"));
const AdminTriageInboxPage = lazy(() => import("@/pages/admin/AdminTriageInboxPage"));
const WhatsAppClinicPage = lazy(() => import("@/pages/admin/WhatsAppClinicPage"));
const AdminPatientVerificationPage = lazy(() => import("@/pages/admin/AdminPatientVerificationPage"));
const AdminRegistrationQRPage = lazy(() => import("@/pages/admin/AdminRegistrationQRPage"));
const PatientAppointmentsPage = lazy(() =>
  import("@/pages/patient/PatientPages").then((m) => ({ default: m.PatientAppointmentsPage }))
);
const PatientBookingPage = lazy(() =>
  import("@/pages/patient/PatientPages").then((m) => ({ default: m.PatientBookingPage }))
);
const PatientHistoryPage = lazy(() =>
  import("@/pages/patient/PatientPages").then((m) => ({ default: m.PatientHistoryPage }))
);
const PatientContactPage = lazy(() =>
  import("@/pages/patient/PatientPages").then((m) => ({ default: m.PatientContactPage }))
);
const PatientProfilePage = lazy(() =>
  import("@/pages/patient/PatientPages").then((m) => ({ default: m.PatientProfilePage }))
);
const PatientHealthCardPage = lazy(() => import("@/pages/patient/PatientHealthCardPage"));
const PatientPaymentsPage = lazy(() => import("@/pages/patient/PatientPaymentsPage"));
const PatientDocumentViewerPage = lazy(() =>
  import("@/pages/patient/PatientDocumentViewerPage")
);
const PatientDicomViewerPage = lazy(() => import("@/pages/patient/PatientDicomViewerPage"));
const KioskScreen = lazy(() => import("@/pages/public/KioskScreen"));
const BrochureViewerPage = lazy(() => import("@/pages/public/BrochureViewerPage"));
const ErasureVerifyPage = lazy(() =>
  import("@/pages/public/ErasureRequestPage").then((m) => ({ default: m.ErasureVerifyPage }))
);

const AppointmentsPage = lazy(() => import("@/pages/admin/AppointmentsPage"));
const InsurancesPage = lazy(() => import("@/pages/admin/InsurancesPage"));
const LocationsPage = lazy(() => import("@/pages/admin/LocationsPage"));
const BillingPage = lazy(() => import("@/pages/admin/BillingPage"));
const BillingAuditPage = lazy(() => import("@/pages/admin/BillingAuditPage"));
const ExpensesPage = lazy(() => import("@/pages/admin/ExpensesPage"));
const AyudantiasPage = lazy(() => import("@/pages/admin/AyudantiasPage"));
const DevCostsPage = lazy(() => import("@/pages/admin/DevCostsPage"));
const StatsPage = lazy(() => import("@/pages/admin/StatsPage"));
const AnalyticsPage = lazy(() => import("@/pages/admin/AnalyticsPage"));
const SettingsPage = lazy(() => import("@/pages/admin/SettingsPage"));
const ProfilePage = lazy(() => import("@/pages/admin/ProfilePage"));
const SignatureHistoryPage = lazy(() => import("@/pages/admin/SignatureHistoryPage"));
const MonitoringPage = lazy(() => import("@/pages/admin/MonitoringPage"));
const ProceduresPage = lazy(() => import("@/pages/admin/ProceduresPage"));
const CollaboratingSurgeonsPage = lazy(() => import("@/pages/admin/CollaboratingSurgeonsPage"));
const CollaboratingSurgeonsReportPage = lazy(() => import("@/pages/admin/CollaboratingSurgeonsReportPage"));
const SurgeriesPage = lazy(() => import("@/pages/admin/SurgeriesPage"));
const SurgeryBudgetsPage = lazy(() => import("@/pages/admin/SurgeryBudgetsPage"));
const StripePaymentsPage = lazy(() => import("@/pages/admin/StripePaymentsPage"));
const WeeklySummaryPage = lazy(() => import("@/pages/admin/WeeklySummaryPage"));
const PatientsPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/admin/PatientsPage"));
const WaitlistPage = lazy(() => import("@/pages/admin/WaitlistPage"));
const UsersPage = lazy(() => import("@/pages/admin/UsersPage"));
const ClinicalHistoryPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/admin/ClinicalHistoryPage"));
const CasuisticaPage = lazy(() => import("@/pages/admin/CasuisticaPage"));
const ReviewsPage = lazy(() => import("@/pages/admin/ReviewsPage"));
const NotificationsPage = lazy(() => import("@/pages/admin/NotificationsPage"));
const TuoTempoPage = lazy(() => import("@/pages/admin/TuoTempoPage"));
const AppointmentManagePage = lazy(() => import("@/pages/admin/AppointmentManagePage"));
const DoctorManagementPage = lazy(() => import("@/pages/admin/DoctorManagementPage"));
const PdfSettingsPage = lazy(() => import("@/pages/admin/PdfSettingsPage"));
const PlanningPage = lazy(() => import("@/pages/admin/PlanningPage"));
const BreastImplantsPage = lazy(() => import("@/pages/admin/BreastImplantsPage"));
const CompliancePage = lazy(() => import("@/pages/admin/CompliancePage"));
const RgpdBreachesPage = lazy(() => import("@/pages/admin/RgpdBreachesPage"));
const AIUsagePage = lazy(() => import("@/pages/admin/AIUsagePage"));
const KioskDevicesPage = lazy(() => import("@/pages/admin/KioskDevicesPage"));
const TvBannerPage = lazy(() => import("@/pages/admin/TvBannerPage"));
const WhatsappQuietHoursPage = lazy(() => import("@/pages/admin/WhatsappQuietHoursPage"));
const NovedadesPage = lazy(() => import("@/pages/admin/NovedadesPage"));
const GalleryPage = lazy(() => import("@/pages/admin/GalleryPage"));
const WaitingRoomPage = lazy(() => import("@/pages/admin/WaitingRoomPage"));
const PatientSharingStatsPage = lazy(() => import("@/pages/admin/PatientSharingStatsPage"));
const PatientAppPage = lazy(() => import("@/pages/admin/PatientAppPage"));
const PortalAnnouncementCampaignPage = lazy(() => import("@/pages/admin/PortalAnnouncementCampaignPage"));
const LaSaludCampaignPage = lazy(() => import("@/pages/admin/LaSaludCampaignPage"));

// Auth Context
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LocationChipsProvider } from "@/components/patients/PatientLocationChips";
import { RgpdProvider } from "@/contexts/RgpdContext";
import { ConfirmDeleteProvider } from "@/contexts/ConfirmDeleteContext";
import RouteSkeleton from "@/components/common/RouteSkeleton";
import BroadcastRefreshListener from "@/components/BroadcastRefreshListener";

// Suspense fallback for lazy routes — route-aware skeleton screens that
// mirror the upcoming page layout. See RouteSkeleton.jsx for the mapping
// rationale and UX research links.
const RouteFallback = () => <RouteSkeleton />;

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Register the currently-running deploy in the persistent history the
  // first time an authenticated admin lands. Idempotent server-side.
  useEffect(() => {
    if (!isAuthenticated) return;
    let done = sessionStorage.getItem('deploy_registered');
    if (done) return;
    (async () => {
      try {
        const r = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!r.ok) return;
        const v = await r.json();
        if (!v?.version) return;
        const api = (await import('@/lib/api')).default;
        await api.post('/deploys/upsert', {
          version: v.version,
          deployedAt: v.deployedAt,
          changes: v.changes || [],
        });
        sessionStorage.setItem('deploy_registered', v.version);
      } catch (_) { /* silent — history is best-effort */ }
    })();
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blush-light">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Admin-only route protection
const AdminOnlyRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  
  if (loading) return null;
  
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-red-400"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
          <h2 className="text-xl font-semibold text-gray-700" data-testid="access-denied-title">Acceso Denegado</h2>
          <p className="text-gray-500">No tiene permisos para acceder a esta sección</p>
        </div>
      </div>
    );
  }
  
  return children;
};

// Superadmin-only route protection (Settings, Backup, User Management)
const SuperAdminRoute = ({ children }) => {
  const { isSuperAdmin, loading } = useAuth();
  
  if (loading) return null;
  
  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-red-400"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
          <h2 className="text-xl font-semibold text-gray-700" data-testid="superadmin-denied-title">Acceso Restringido</h2>
          <p className="text-gray-500">Solo el superadministrador puede acceder a esta sección</p>
        </div>
      </div>
    );
  }
  
  return children;
};

// Permission-based route protection: allows admin + any user with the right permission
const PermissionRoute = ({ children, module, action = 'view' }) => {
  const { isAdmin, loading, admin } = useAuth();
  
  if (loading) return null;
  
  const hasPermission = isAdmin || (admin?.permissions?.[module]?.[action] === true);
  
  if (!hasPermission) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-red-400"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
          <h2 className="text-xl font-semibold text-gray-700" data-testid="access-denied-title">Acceso Denegado</h2>
          <p className="text-gray-500">No tiene permisos para acceder a esta sección</p>
        </div>
      </div>
    );
  }
  
  return children;
};

// Smart Dashboard - renders the right dashboard based on role
const SmartDashboard = () => {
  const { isAdmin, isDoctor, loading } = useAuth();
  if (loading) return null;
  if (isDoctor) return <DoctorDashboardPage />;
  if (isAdmin) return <DashboardPage />;
  // Non-admin, non-doctor (receptionist etc.) - show access denied
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-red-400"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
        <h2 className="text-xl font-semibold text-gray-700">Acceso Denegado</h2>
        <p className="text-gray-500">No tiene permisos para acceder a esta sección</p>
      </div>
    </div>
  );
};

// Global session warning - must be inside AuthProvider
const GlobalSessionWarning = () => {
  const { showSessionWarning, logout } = useAuth();
  return <SessionWarningDialog open={showSessionWarning} onLogout={logout} />;
};

function App() {
  useEffect(() => {
    // Fuera del Área Paciente el splash ya se elimina en index.html.
    // Dentro, lo retira PatientAreaLayout al estar montado; esto es solo
    // un respaldo por si la ruta /mi-area no monta el layout.
    const t = setTimeout(() => window.__removePatientSplash?.(), 4000);
    return () => clearTimeout(t);
  }, []);
  return (
    <AuthProvider>
      <LocationChipsProvider>
      <RgpdProvider>
      <ConfirmDeleteProvider>
        <BrowserRouter>
          <GlobalSessionWarning />
          <BroadcastRefreshListener />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
          {/* Root redirects to public booking page — UNLESS the host is
              klinilog.{com,app,es,eu}, in which case we serve the marketing
              landing instead. Detection happens at runtime via the browser
              hostname so a single deployment can serve BOTH the clinical app
              and the Klinilog SaaS landing depending on which domain the
              visitor used. */}
          <Route
            path="/"
            element={
              /klinilog\.(com|app|es|eu)$/i.test(window.location.hostname)
                ? <KlinilogLandingPage />
                : <Navigate to="/booking" replace />
            }
          />
          {/* Explicit route so we can preview the landing on any host
              (e.g. citas.drmarcosadrianzen.com/klinilog or the preview URL). */}
          <Route path="/klinilog" element={<KlinilogLandingPage />} />
          
          {/* Public Routes - accessible via /booking */}
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/lista-espera/confirmar/:token" element={
            <Suspense fallback={null}><VoluntaryConfirmPage /></Suspense>
          } />
          <Route path="/lista-espera/ofertas/:token" element={
            <Suspense fallback={null}><VoluntaryOffersPage /></Suspense>
          } />
          <Route path="/consejos/:slug" element={<PublicArticlePage />} />
          <Route path="/dr-marcos-adrianzen" element={
            <div className="min-h-screen bg-[#FBF6F8]">
              <Suspense fallback={null}><PatientDoctorPage publicMode /></Suspense>
            </div>
          } />
          <Route path="/resultados" element={<PublicResultsPage />} />
          <Route path="/booking/success" element={<BookingSuccessPage />} />
          <Route path="/booking/cancel" element={<BookingCancelPage />} />
          <Route path="/cancel/:token" element={<CancelAppointmentPage />} />
          
          {/* Remote Signature - public route for patients */}
          <Route path="/firmar/:token" element={<RemoteSignaturePage />} />

          {/* Brochure viewer - opened from the `brochure_send` WhatsApp button */}
          <Route path="/triptico/:token" element={<BrochureViewerPage />} />

          <Route path="/presupuesto/aceptar/:token" element={<AcceptBudgetPage />} />

          {/* Public RGPD compliance seal — trust page for patients & auditors */}
          <Route path="/cumplimiento" element={<CompliancePublicPage />} />
          <Route path="/privacidad-app" element={<AppPrivacyPage />} />

          {/* Public RGPD Right to Erasure (Art. 17) form + email verification */}
          <Route path="/derecho-al-olvido" element={<ErasureRequestPage />} />
          <Route path="/derecho-al-olvido/confirmar/:token" element={<ErasureVerifyPage />} />
          
          {/* Review Confirmation - public route for patients */}
          <Route path="/review-confirm/:token" element={<ReviewConfirmPage />} />

          {/* Mobile Upload - public, token-based */}
          <Route path="/mobile-upload/:token" element={<MobileUploadPage />} />
          
          {/* Patient confirmation/cancellation from email link */}
          <Route path="/manage-appointment/:manageToken" element={<PatientConfirmPage />} />

          {/* SMS share-link: public PDF delivery (recetas, consentimientos, presupuestos…). */}
          <Route path="/d/:token" element={<PublicDocumentPage />} />

          {/* Patient self-service portal — permanent URL, lists all the patient's documents. */}
          <Route path="/portal/:token" element={<PatientPortalPage />} />

          {/* Área Paciente ("Unidad de Mama Dr. Marcos Adrianzén") — PWA
              con login OTP, agenda, check-in y documentos compartidos.
              ErrorBoundary externo: captura ChunkLoadError si un cliente
              con la PWA abierta desde antes del deploy pide un chunk con
              hash viejo ya reemplazado en el CDN → auto-reload para que
              el paciente NO vea pantalla en blanco. */}
          <Route path="/mi-area" element={
            <ErrorBoundary>
              <PatientAreaLayout />
            </ErrorBoundary>
          }>
            <Route index element={<PatientHomePage />} />
            <Route path="login" element={<PatientLoginPage />} />
            <Route path="registro" element={<PatientRegisterPage />} />
            <Route path="enter/:token" element={<PatientMagicLinkEnterPage />} />
            <Route path="citas" element={<PatientAppointmentsPage />} />
            <Route path="pedir-cita" element={<PatientBookingPage />} />
            <Route path="mi-historia" element={<PatientHistoryPage />} />
            <Route path="consejos" element={<PatientContentPage />} />
            <Route path="consejos/:slug" element={<PatientArticlePage />} />
            <Route path="guia-sujetador" element={<PatientBraGuidePage />} />
            <Route path="calculadora-riesgo" element={<PatientRiskCalculatorPage />} />
            <Route path="autoexploracion" element={<PatientSelfExamPage />} />
            <Route path="diario-postop" element={<PatientPostopDiaryPage />} />
            <Route path="avisos" element={<PatientNotificationsPage />} />
            <Route path="urgencia" element={<PatientUrgentTriagePage />} />
            <Route path="imagen/:studyId" element={<PatientDicomViewerPage />} />
            <Route path="contacto" element={<PatientContactPage />} />
            <Route path="conoce-a-tu-doctor" element={<PatientDoctorPage />} />
            <Route path="mi-ficha" element={<PatientProfilePage />} />
            <Route path="mi-carne" element={<PatientHealthCardPage />} />
            <Route path="pagos" element={<PatientPaymentsPage />} />
            <Route path="documento/:type/:id" element={<PatientDocumentViewerPage />} />
          </Route>

          {/* Kiosk mode — persistent signature pad for paired tablets (no auth, token in URL/localStorage) */}
          <Route path="/kiosk" element={<KioskScreen />} />
          
          {/* Appointment Manage - accessible via email link (token-based) */}
          <Route path="/admin/manage/:manageToken" element={<AppointmentManagePage />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<LoginPage />} />
          
          <Route path="/admin" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <ConnectionBanner />
                <AdminLayout />
              </ErrorBoundary>
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/admin/agenda" replace />} />
            {/* Dashboard - Admin sees full dashboard, Doctor sees personalized dashboard */}
            <Route path="dashboard" element={<SmartDashboard />} />
            <Route path="agenda" element={<AgendaPage />} />
            <Route path="appointments" element={<AppointmentsPage />} />
            <Route path="waitlist" element={<WaitlistPage />} />
            <Route path="patients" element={<PatientsPage />} />
            <Route path="clinical-history" element={<PermissionRoute module="clinical_history"><ClinicalHistoryPage /></PermissionRoute>} />
            <Route path="novedades" element={<NovedadesPage />} />
            <Route path="surgeries" element={<SurgeriesPage />} />
            <Route path="casuistica" element={<CasuisticaPage />} />
            <Route path="presupuestos" element={<SurgeryBudgetsPage />} />
            <Route path="surgery-budgets" element={<SurgeryBudgetsPage />} />
            <Route path="pagos-stripe" element={<PermissionRoute module="admin_pages" action="stripe_payments"><StripePaymentsPage /></PermissionRoute>} />
            <Route path="planning" element={<PlanningPage />} />
            <Route path="weekly-summary" element={<WeeklySummaryPage />} />
            <Route path="procedures" element={<PermissionRoute module="prices"><ProceduresPage /></PermissionRoute>} />
            <Route path="collaborating-surgeons" element={<PermissionRoute module="admin_pages" action="collaborating_surgeons"><CollaboratingSurgeonsPage /></PermissionRoute>} />
            <Route path="collaborating-surgeons/report" element={<PermissionRoute module="admin_pages" action="collaborating_surgeons_report"><CollaboratingSurgeonsReportPage /></PermissionRoute>} />
            <Route path="motiva-catalog" element={<BreastImplantsPage />} />
            <Route path="protesis-mama" element={<BreastImplantsPage />} />
            <Route path="breast-implants" element={<BreastImplantsPage />} />
            <Route path="gallery" element={<PermissionRoute module="admin_pages" action="gallery"><GalleryPage /></PermissionRoute>} />
            <Route path="sala-espera" element={<PermissionRoute module="admin_pages" action="waiting_room"><WaitingRoomPage /></PermissionRoute>} />
            <Route path="triage" element={<PermissionRoute module="admin_pages" action="urgent_triage_inbox"><AdminTriageInboxPage /></PermissionRoute>} />
            <Route path="whatsapp" element={<PermissionRoute module="admin_pages" action="whatsapp_clinic"><WhatsAppClinicPage /></PermissionRoute>} />
            <Route path="patient-verification" element={<PermissionRoute module="admin_pages" action="patient_verification"><AdminPatientVerificationPage /></PermissionRoute>} />
            <Route path="qr-registro" element={<PermissionRoute module="admin_pages" action="registration_qr"><AdminRegistrationQRPage /></PermissionRoute>} />
            <Route path="stats-compartidos" element={<PermissionRoute module="admin_pages" action="sharing_stats"><PatientSharingStatsPage /></PermissionRoute>} />
            <Route path="patient-app" element={<PermissionRoute module="admin_pages" action="sharing_stats"><PatientAppPage /></PermissionRoute>} />
            <Route path="testimonios" element={<PermissionRoute module="admin_pages" action="sharing_stats"><TestimonialsAdminPage /></PermissionRoute>} />
            <Route path="anuncio-area-paciente" element={<PermissionRoute module="admin_pages" action="portal_announcement"><PortalAnnouncementCampaignPage /></PermissionRoute>} />
            <Route path="aviso-la-salud" element={<PermissionRoute module="admin_pages" action="clinic_move_campaign"><LaSaludCampaignPage /></PermissionRoute>} />
            <Route path="insurances" element={<InsurancesPage />} />
            <Route path="locations" element={<PermissionRoute module="admin_pages" action="locations"><LocationsPage /></PermissionRoute>} />
            {/* Billing - doctors see their own, admin sees all */}
            <Route path="billing" element={<PermissionRoute module="billing"><BillingPage /></PermissionRoute>} />
            <Route path="billing-audit" element={<PermissionRoute module="admin_pages" action="billing_audit"><BillingAuditPage /></PermissionRoute>} />
            <Route path="expenses" element={<PermissionRoute module="billing"><ExpensesPage /></PermissionRoute>} />
            <Route path="ayudantias" element={<SuperAdminRoute><AyudantiasPage /></SuperAdminRoute>} />
            <Route path="costes-programa" element={<SuperAdminRoute><DevCostsPage /></SuperAdminRoute>} />
            {/* Admin-only routes */}
            <Route path="stats" element={<PermissionRoute module="admin_pages" action="stats"><StatsPage /></PermissionRoute>} />
            <Route path="analytics" element={<PermissionRoute module="billing"><AnalyticsPage /></PermissionRoute>} />
            <Route path="reviews" element={<PermissionRoute module="admin_pages" action="reviews"><ReviewsPage /></PermissionRoute>} />
            <Route path="notifications" element={<PermissionRoute module="admin_pages" action="notifications"><NotificationsPage /></PermissionRoute>} />
            <Route path="tuotempo" element={<PermissionRoute module="admin_pages" action="tuotempo"><TuoTempoPage /></PermissionRoute>} />
            <Route path="settings" element={<SuperAdminRoute><SettingsPage /></SuperAdminRoute>} />
            <Route path="profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="signature-history" element={<SignatureHistoryPage />} />
            <Route path="monitoring" element={<PermissionRoute module="admin_pages" action="monitoring"><MonitoringPage /></PermissionRoute>} />
            <Route path="doctors" element={<PermissionRoute module="admin_pages" action="doctors_management"><DoctorManagementPage /></PermissionRoute>} />
            <Route path="pdf-settings" element={<PermissionRoute module="admin_pages" action="pdf_settings"><PdfSettingsPage /></PermissionRoute>} />
            <Route path="users" element={<SuperAdminRoute><UsersPage /></SuperAdminRoute>} />
            <Route path="compliance" element={<SuperAdminRoute><CompliancePage /></SuperAdminRoute>} />
            <Route path="rgpd-breaches" element={<SuperAdminRoute><RgpdBreachesPage /></SuperAdminRoute>} />
            <Route path="klinilog-leads" element={<SuperAdminRoute><KlinilogLeadsPage /></SuperAdminRoute>} />
            <Route path="ai-usage" element={<PermissionRoute module="admin_pages" action="ai_usage"><AIUsagePage /></PermissionRoute>} />
            <Route path="kiosk-devices" element={<PermissionRoute module="admin_pages" action="kiosk_devices"><KioskDevicesPage /></PermissionRoute>} />
            <Route path="tv-banner" element={<PermissionRoute module="admin_pages" action="tv_banner"><TvBannerPage /></PermissionRoute>} />
            <Route path="whatsapp-quiet-hours" element={<PermissionRoute module="admin_pages" action="whatsapp_quiet_hours"><WhatsappQuietHoursPage /></PermissionRoute>} />
          </Route>
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </ConfirmDeleteProvider>
      </RgpdProvider>
      </LocationChipsProvider>
    </AuthProvider>
  );
}

export default App;
